import { isPresent } from "@mixitone/util";
import { observable } from "@nx-js/observer-util";
import Debug from "debug";
import { AssignableModelFields } from "./config";
import { GetModelConfig, Model, ModelConstructor } from "./model";
import { Query, SubscriptionCallbackPayload } from "./query";

const debug = Debug("oom:collection");

export type CollectionDescriptor<M extends Model<any>> = {
  model: () => ModelConstructor<M>;
  key: string;
  order?: string;
};
export type Collections = {
  [index: string]: CollectionDescriptor<any>;
};

interface SubscriptionOptions<M extends Model<any>> {
  insert?: boolean;
  update?: boolean;
  ignoreSelfBroadcast?: boolean;
  callback?: (data: SubscriptionCallbackPayload<M>[]) => Promise<void>;
}

export type Collection<M extends Model<any>> = Array<M> & {
  query: Query<M, GetModelConfig<M>>;
  /**
   * Load the collection from the database
   */
  load(): Promise<void>;
  /**
   * Load the count of the collection from the database
   */
  loadCount(): Promise<void>;
  /**
   * Reload the collection from the database
   */
  reload(): Promise<void>;
  /**
   * Wait for the collection to load. Assumes other code is going to call load()
   */
  wait(): Promise<void>;
  /**
   * Has the collection been loaded?
   */
  loaded: boolean;
  /**
   * Set the collection to the given data
   */
  set(data: Array<M>): void;
  /**
   * find a record by id
   */
  findById(id: string): M | undefined;
  /**
   * Check if we have an id
   */
  hasId(id: string): boolean;
  /**
   * Remove a record by id
   */
  removeById(id: string): void;
  /**
   * Create a new, saved, record in the collection
   */
  create(data: AssignableModelFields<GetModelConfig<M>>): Promise<M>;
  /**
   * Add a record to the collection if it doesn't already exist
   */
  add(record: M): void;
  /**
   * Save all records in the collection
   */
  save(): Promise<void>;

  subscribe(options: SubscriptionOptions<M>): Promise<() => void>;

  count(): number;
};

export function createCollection<M extends Model<any>, D extends CollectionDescriptor<M>>(
  on: M,
  key: string,
  ref: D,
): Collection<M> {
  const container: Collection<M> = observable([]) as any;
  container.loaded = false;
  let loading = false;
  let loadingCount = false;
  const onLoad: Array<(value: any) => void> = [];
  const onLoaded = () => {
    while (onLoad.length > 0) {
      onLoad.shift()!(void 0);
    }
  };
  // This lets us know if we're the latest load request and cancel otherwise
  let loadingId = 0;

  const cancel = () => {
    loadingId += 1;
    loading = false;
  };

  Object.defineProperty(container, "query", {
    get: (): Query<any, any> => {
      let query = ref
        .model()
        .query()
        .eq(ref.key, on.id as any);
      if (ref.order) {
        query = query.orderBy(ref.order, "asc");
      }
      return query;
    },
  });
  Object.defineProperty(container, "load", {
    value: async function load(this: Collection<any>) {
      if (container.loaded) return;
      if (loading) {
        return new Promise((resolve) => {
          onLoad.push(resolve);
        });
      }

      // Prevent overwriting manually added records
      if (container.length > 0) throw new Error(`Records already added to ref ${key}`);

      loading = true;
      // Increment the loading count and store it for a later check
      let myLoadingCount = (loadingId += 1);

      const records = await this.query.all();

      // If the loading count has changed, we're not the latest load request
      if (myLoadingCount !== loadingId) return;

      container.push(...(records as any));
      container.loaded = true;
      loading = false;

      // unshift each callback and call it
      onLoaded();
    },
  });
  Object.defineProperty(container, "loadCount", {
    value: async function loadCount(this: Collection<any>) {
      if (this.loaded) return;
      const tableName = ref.model().tableName;

      if (isPresent(on.data.get(tableName)?.count)) return;
      if (loading || loadingCount) return;
      loadingCount = true;

      const query = await ref
        .model()
        .query()
        .eq(ref.key, on.id as any)
        .count();
      if (!on.data.has(tableName)) on.data.set(tableName, []);
      on.data.get(tableName).push({ count: query });

      loadingCount = false;
    },
  });
  Object.defineProperty(container, "count", {
    value: function count(this: Collection<any>) {
      if (this.loaded) {
        return this.length;
      } else {
        const data = on.data.get(key);

        if (isPresent(data?.[0]?.count)) {
          return data[0].count as number;
        }
      }

      throw new Error(`Cannot get count of ${key}, collection not loaded`);
    },
  });
  container.wait = async () => {
    if (container.loaded) return;
    return new Promise((resolve) => {
      onLoad.push(resolve);
    });
  };
  container.reload = async () => {
    cancel();
    container.loaded = false;
    container.splice(0, container.length);
    await container.load();
  };
  container.set = (data: any[]) => {
    cancel();
    const wasLoaded = container.loaded;
    container.loaded = true;
    container.splice(0, container.length);
    container.push(...data);

    if (!wasLoaded) {
      onLoaded();
    }
  };
  container.findById = (id: string) => {
    return container.find((r) => r.id === id);
  };
  container.hasId = (id: string) => {
    return container.some((r) => r.id === id);
  };
  container.removeById = (id: string) => {
    const index = container.findIndex((r) => r.id === id);
    if (index === -1) return;
    container.splice(index, 1);
  };
  container.save = async () => {
    debug("Saving collection", key);
    await Promise.all(container.map((r) => r.save()));
  };
  container.create = async (data: AssignableModelFields<GetModelConfig<M>>) => {
    const record = new (ref.model())(data);
    record.writeAttribute(ref.key, on.uniqueId);
    await record.save();
    container.add(record);
    return record;
  };
  container.add = (record: M) => {
    record.writeAttribute(ref.key, on.uniqueId);

    if (container.hasId(record.uniqueId)) {
      const index = container.findIndex((r) => r.uniqueId === record.uniqueId);
      container.splice(index, 1, record);
      return;
    }

    const order = ref.order;

    // Directly push the record if there's no order specified
    if (!order) {
      container.push(record);
      return;
    }

    // Find the position for insertion based on the order key
    const position = container.findIndex((item) => record.readAttribute(order) < item.readAttribute(order));

    // Insert the record at the found position or push to the end if not found
    if (position === -1) {
      container.push(record);
    } else {
      container.splice(position, 0, record);
    }
  };
  container.subscribe = async (options: SubscriptionOptions<M>) => {
    if (!container.loaded && !loading) throw new Error("Cannot subscribe to unloaded collection");
    await container.wait();
    return await container.query.subscribe({
      ignoreSelfBroadcast: options.ignoreSelfBroadcast,
      callback: async (updates) => {
        if (options.callback) {
          await options.callback(updates);
        }

        for (const update of updates) {
          if (options.insert && update.type === "INSERT" && container.query.matches(update.model)) {
            container.add(update.model);
          }

          if (update.type === "DELETE") {
            container.removeById(update.model.uniqueId);
          }

          if (options.update && update.type === "UPDATE") {
            if (container.hasId(update.model.uniqueId)) {
              if (!container.query.matches(update.model)) {
                container.removeById(update.model.uniqueId);
              } else {
                const record = container.findById(update.model.uniqueId)!;
                record.updateFromSubscription(update.model.rawAttributes, update.source);
              }
            } else {
              if (container.query.matches(update.model)) {
                container.push(update.model);
              }
            }
          }
        }
      },
    });
  };

  return container;
}
